import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export function SignupForm({ eventId, api }) {
    const queryClient = useQueryClient();
    const [participantName, setParticipantName] = useState('');
    const [homeTown, setHomeTown] = useState('');
    const [horses, setHorses] = useState(['']);
    const [classEntries, setClassEntries] = useState([]);
    const [totalCost, setTotalCost] = useState(0);
    const [costBreakdown, setCostBreakdown] = useState([]);
    const [isCalculating, setIsCalculating] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [signupData, setSignupData] = useState(null);

    // Fetch event details
    const { data: event, isLoading: isLoadingEvent } = useQuery({
        queryKey: ['event', eventId],
        queryFn: () => api.getEvent(eventId),
        enabled: !!eventId,
    });

    // Fetch classes for this event
    const { data: classes, isLoading: isLoadingClasses } = useQuery({
        queryKey: ['classes', eventId],
        queryFn: () => api.getClasses(eventId),
        enabled: !!eventId,
    });

    // Fetch class details with entry fees
    const { data: classDetails, isLoading: isLoadingClassDetails } = useQuery({
        queryKey: ['classDetails', eventId],
        queryFn: async () => {
            if (!classes) return {};
            const details = {};
            for (const classInfo of classes) {
                try {
                    const classDetail = await api.getClassDetails(classInfo.id);
                    console.log(`Class ${classInfo.id} details:`, classDetail);
                    details[classInfo.id] = classDetail;
                } catch (error) {
                    console.error(`Failed to fetch details for class ${classInfo.id}:`, error);
                    details[classInfo.id] = { entryFee: 0, divisions: [] };
                }
            }
            console.log('All class details:', details);
            return details;
        },
        enabled: !!classes && classes.length > 0,
    });

    // Calculate cost on the frontend
    useEffect(() => {
        if (classEntries.length > 0 && horses.some(h => h.trim() !== '')) {
            const validEntries = classEntries.filter(entry =>
                entry.classId && entry.selectedHorses && entry.selectedHorses.length > 0
            );

            if (validEntries.length > 0) {
                let total = 0;
                let breakdown = [];
                for (const entry of validEntries) {
                    const details = classDetails?.[entry.classId];
                    let entryFee = details?.entryFee || 0;
                    if (Array.isArray(entryFee)) entryFee = entryFee[1] || 0;
                    entryFee = parseFloat(entryFee) || 0;
                    const entryCost = entryFee * entry.selectedHorses.length;
                    total += entryCost;
                    breakdown.push({
                        classId: entry.classId,
                        selectedHorses: entry.selectedHorses,
                        entryFee,
                        entryCost
                    });
                }
                setTotalCost(total);
                setCostBreakdown(breakdown);
            } else {
                setTotalCost(0);
                setCostBreakdown([]);
            }
        } else {
            setTotalCost(0);
            setCostBreakdown([]);
        }
    }, [classEntries, horses, classDetails]);

    // Create signup mutation
    const createSignupMutation = useMutation({
        mutationFn: (signupData) => api.createSignup(signupData),
        onSuccess: (data) => {
            // Reset form
            setParticipantName('');
            setHomeTown('');
            setHorses(['']);
            setClassEntries([]);
            setTotalCost(0);
            setCostBreakdown([]);
            setErrors({});
            setIsSubmitting(false);

            // Show success state
            setSignupData(data);
            setShowSuccess(true);
        },
        onError: (error) => {
            setIsSubmitting(false);
            setErrors({ submit: error.message });
        },
    });

    // Validate form fields
    const validateForm = () => {
        const newErrors = {};

        if (!participantName.trim()) {
            newErrors.participantName = 'Rider name is required';
        }

        if (!homeTown.trim()) {
            newErrors.homeTown = 'Home town is required';
        }

        const validHorses = horses.filter(h => h.trim() !== '');
        if (validHorses.length === 0) {
            newErrors.horses = 'At least one horse is required';
        }

        const validEntries = classEntries.filter(entry =>
            entry.classId && entry.selectedHorses && entry.selectedHorses.length > 0
        );
        if (validEntries.length === 0) {
            newErrors.classEntries = 'At least one class entry is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Add a new horse field
    const addHorse = () => {
        setHorses([...horses, '']);
        // Clear horse-related errors when adding a horse
        if (errors.horses) {
            setErrors(prev => ({ ...prev, horses: undefined }));
        }
    };

    // Remove a horse field
    const removeHorse = (index) => {
        if (horses.length > 1) {
            const newHorses = horses.filter((_, i) => i !== index);
            setHorses(newHorses);
        }
    };

    // Update horse name
    const updateHorse = (index, value) => {
        const newHorses = [...horses];
        newHorses[index] = value;
        setHorses(newHorses);
        // Clear horse-related errors when updating
        if (errors.horses) {
            setErrors(prev => ({ ...prev, horses: undefined }));
        }
    };

    // Add a class entry
    const addClassEntry = () => {
        if (!classes || classes.length === 0) return;

        const newEntry = {
            classId: classes[0].id,
            selectedHorses: []
        };
        setClassEntries([...classEntries, newEntry]);
        // Clear class entry errors when adding
        if (errors.classEntries) {
            setErrors(prev => ({ ...prev, classEntries: undefined }));
        }
    };

    // Remove a class entry
    const removeClassEntry = (index) => {
        setClassEntries(classEntries.filter((_, i) => i !== index));
    };

    // Update class entry
    const updateClassEntry = (index, field, value) => {
        const newEntries = [...classEntries];
        newEntries[index] = { ...newEntries[index], [field]: value };
        setClassEntries(newEntries);
        // Clear class entry errors when updating
        if (errors.classEntries) {
            setErrors(prev => ({ ...prev, classEntries: undefined }));
        }
    };

    // Toggle horse selection for a class
    const toggleHorseSelection = (classIndex, horseName) => {
        const entry = classEntries[classIndex];
        const selectedHorses = entry.selectedHorses || [];

        if (selectedHorses.includes(horseName)) {
            // Remove horse
            const newSelectedHorses = selectedHorses.filter(h => h !== horseName);
            updateClassEntry(classIndex, 'selectedHorses', newSelectedHorses);
        } else {
            // Add horse
            const newSelectedHorses = [...selectedHorses, horseName];
            updateClassEntry(classIndex, 'selectedHorses', newSelectedHorses);
        }
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!validateForm()) {
            setIsSubmitting(false);
            return;
        }

        const validHorses = horses.filter(h => h.trim() !== '');
        const validEntries = classEntries.filter(entry =>
            entry.classId && entry.selectedHorses && entry.selectedHorses.length > 0
        );

        const signupData = {
            participantName: participantName.trim(),
            homeTown: homeTown.trim(),
            horses: validHorses,
            classEntries: validEntries
        };

        createSignupMutation.mutate(signupData);
    };

    if (isLoadingEvent || isLoadingClasses) {
        return (
            <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" aria-hidden="true"></div>
                <span className="ml-2">Loading event details...</span>
            </div>
        );
    }

    if (!event || !classes) {
        return (
            <div className="text-center py-8" role="alert">
                <p className="text-muted-foreground">Event not found or no classes available.</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-6">
            <form
                onSubmit={handleSubmit}
                className="space-y-8"
                aria-label={`Signup form for ${event.name}`}
                noValidate
            >
                {/* Error Summary */}
                {Object.keys(errors).length > 0 && (
                    <div
                        className="rounded-lg border border-destructive bg-destructive/10 p-4"
                        role="alert"
                        aria-live="assertive"
                    >
                        <h2 className="text-lg font-semibold text-destructive mb-2">Please fix the following errors:</h2>
                        <ul className="list-disc list-inside space-y-1">
                            {Object.entries(errors).map(([field, message]) => (
                                <li key={field} className="text-destructive">
                                    {message}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Participant Information */}
                <fieldset className="rounded-lg border bg-card p-6 shadow-sm">
                    <legend className="text-xl font-semibold text-foreground mb-4 px-2">Participant Information</legend>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label htmlFor="participant-name" className="text-sm font-medium">
                                Rider Name <span className="text-destructive" aria-label="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="participant-name"
                                name="participantName"
                                value={participantName}
                                onChange={(e) => {
                                    setParticipantName(e.target.value);
                                    if (errors.participantName) {
                                        setErrors(prev => ({ ...prev, participantName: undefined }));
                                    }
                                }}
                                required
                                aria-required="true"
                                aria-invalid={!!errors.participantName}
                                aria-describedby={errors.participantName ? "participant-name-error" : undefined}
                                className={`flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.participantName ? 'border-destructive' : 'border-input bg-background'
                                    }`}
                                autoComplete="name"
                                autoFocus
                            />
                            {errors.participantName && (
                                <p id="participant-name-error" className="text-sm text-destructive" role="alert">
                                    {errors.participantName}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="home-town" className="text-sm font-medium">
                                Home Town <span className="text-destructive" aria-label="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="home-town"
                                name="homeTown"
                                value={homeTown}
                                onChange={(e) => {
                                    setHomeTown(e.target.value);
                                    if (errors.homeTown) {
                                        setErrors(prev => ({ ...prev, homeTown: undefined }));
                                    }
                                }}
                                required
                                aria-required="true"
                                aria-invalid={!!errors.homeTown}
                                aria-describedby={errors.homeTown ? "home-town-error" : undefined}
                                className={`flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${errors.homeTown ? 'border-destructive' : 'border-input bg-background'
                                    }`}
                                autoComplete="address-level2"
                            />
                            {errors.homeTown && (
                                <p id="home-town-error" className="text-sm text-destructive" role="alert">
                                    {errors.homeTown}
                                </p>
                            )}
                        </div>
                    </div>
                </fieldset>

                {/* Horses */}
                <fieldset className="rounded-lg border bg-card p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <legend className="text-xl font-semibold text-foreground px-2">Horses</legend>
                        <button
                            type="button"
                            onClick={addHorse}
                            aria-label="Add a new horse"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3"
                        >
                            Add Horse
                        </button>
                    </div>

                    {errors.horses && (
                        <p className="text-sm text-destructive mb-3" role="alert">
                            {errors.horses}
                        </p>
                    )}

                    <div className="space-y-3" role="group" aria-labelledby="horses-group">
                        <div id="horses-group" className="sr-only">Horse entries</div>
                        {horses.map((horse, index) => (
                            <div key={index} className="flex items-center space-x-3">
                                <input
                                    type="text"
                                    id={`horse-${index}`}
                                    name={`horse-${index}`}
                                    value={horse}
                                    onChange={(e) => updateHorse(index, e.target.value)}
                                    placeholder="Horse name"
                                    required
                                    aria-required="true"
                                    aria-label={`Horse ${index + 1} name`}
                                    className="flex-1 h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                    autoComplete="off"
                                />
                                {horses.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeHorse(index)}
                                        aria-label={`Remove horse ${index + 1}`}
                                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 w-8"
                                    >
                                        <span aria-hidden="true">×</span>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </fieldset>

                {/* Class Entries */}
                <fieldset className="rounded-lg border bg-card p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <legend className="text-xl font-semibold text-foreground px-2">Class Entries</legend>
                        <button
                            type="button"
                            onClick={addClassEntry}
                            disabled={classes.length === 0}
                            aria-label="Add a new class entry"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-3"
                        >
                            Add Class
                        </button>
                    </div>

                    {errors.classEntries && (
                        <p className="text-sm text-destructive mb-3" role="alert">
                            {errors.classEntries}
                        </p>
                    )}

                    {classEntries.length === 0 ? (
                        <p className="text-muted-foreground">No class entries added yet.</p>
                    ) : (
                        <div className="space-y-6" role="group" aria-labelledby="class-entries-group">
                            <div id="class-entries-group" className="sr-only">Class entry selections</div>
                            {classEntries.map((entry, index) => (
                                <fieldset key={index} className="border rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <legend className="text-lg font-medium">Class Entry {index + 1}</legend>
                                        <button
                                            type="button"
                                            onClick={() => removeClassEntry(index)}
                                            aria-label={`Remove class entry ${index + 1}`}
                                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-8 w-8"
                                        >
                                            <span aria-hidden="true">×</span>
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label htmlFor={`class-select-${index}`} className="text-sm font-medium">
                                                Select Class <span className="text-destructive" aria-label="required">*</span>
                                            </label>
                                            <select
                                                id={`class-select-${index}`}
                                                name={`class-${index}`}
                                                value={entry.classId || ''}
                                                onChange={(e) => updateClassEntry(index, 'classId', e.target.value)}
                                                required
                                                aria-required="true"
                                                aria-label={`Select class for entry ${index + 1}`}
                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                                            >
                                                <option value="">Choose a class...</option>
                                                {classes.map((classInfo) => {
                                                    const details = classDetails?.[classInfo.id];
                                                    let entryFee = details?.entryFee || 0;
                                                    // Handle the case where entryFee might be an array
                                                    if (Array.isArray(entryFee)) {
                                                        entryFee = entryFee[1] || 0;
                                                    }
                                                    entryFee = parseFloat(entryFee) || 0;
                                                    console.log(`Rendering class ${classInfo.id}:`, { details, entryFee });
                                                    return (
                                                        <option key={classInfo.id} value={classInfo.id}>
                                                            {classInfo.name} - ${entryFee.toFixed(2)} per horse
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>

                                        {entry.classId && horses.some(h => h.trim() !== '') && (
                                            <fieldset className="space-y-2">
                                                <legend className="text-sm font-medium">
                                                    Select Horses <span className="text-destructive" aria-label="required">*</span>
                                                </legend>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" role="group" aria-labelledby={`horses-selection-${index}`}>
                                                    <div id={`horses-selection-${index}`} className="sr-only">
                                                        Horse selection for class entry {index + 1}
                                                    </div>
                                                    {horses.filter(h => h.trim() !== '').map((horse, horseIndex) => (
                                                        <label key={horseIndex} className="flex items-center space-x-2">
                                                            <input
                                                                type="checkbox"
                                                                name={`horse-selection-${index}-${horseIndex}`}
                                                                checked={entry.selectedHorses?.includes(horse) || false}
                                                                onChange={() => toggleHorseSelection(index, horse)}
                                                                aria-label={`Select ${horse} for class entry ${index + 1}`}
                                                                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                                            />
                                                            <span className="text-sm">{horse}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </fieldset>
                                        )}
                                    </div>
                                </fieldset>
                            ))}
                        </div>
                    )}
                </fieldset>

                {/* Cost Summary */}
                {(totalCost > 0 || isCalculating) && (
                    <div className="rounded-lg border bg-card p-6 shadow-sm" role="region" aria-label="Cost summary">
                        <h2 className="text-xl font-semibold text-foreground mb-4">Cost Summary</h2>

                        {isCalculating ? (
                            <div className="flex items-center space-x-2" role="status" aria-live="polite">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" aria-hidden="true"></div>
                                <span className="text-sm text-muted-foreground">Calculating...</span>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {costBreakdown.map((item, index) => {
                                    const classInfo = classes.find(c => c.id === item.classId);
                                    const details = classDetails?.[item.classId];
                                    let entryFee = details?.entryFee || 0;
                                    // Handle the case where entryFee might be an array
                                    if (Array.isArray(entryFee)) {
                                        entryFee = entryFee[1] || 0;
                                    }
                                    entryFee = parseFloat(entryFee) || 0;
                                    return (
                                        <div key={index} className="flex justify-between items-center py-2 border-b border-border last:border-b-0">
                                            <div>
                                                <p className="font-medium">{classInfo?.name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    {item.selectedHorses.join(', ')} - ${entryFee.toFixed(2)} each
                                                </p>
                                            </div>
                                            <p className="font-semibold">${item.entryCost.toFixed(2)}</p>
                                        </div>
                                    );
                                })}

                                <div className="flex justify-between items-center pt-3 border-t border-border">
                                    <p className="text-lg font-semibold">Total Cost</p>
                                    <p className="text-lg font-bold text-primary" aria-live="polite">
                                        ${totalCost.toFixed(2)}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={createSignupMutation.isPending || totalCost === 0 || isSubmitting}
                        aria-describedby="submit-status"
                        className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8"
                    >
                        {createSignupMutation.isPending || isSubmitting ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" aria-hidden="true"></div>
                                Processing...
                            </>
                        ) : (
                            `Complete Signup - $${totalCost.toFixed(2)}`
                        )}
                    </button>
                </div>

                <div id="submit-status" className="sr-only" aria-live="polite">
                    {createSignupMutation.isPending || isSubmitting ? 'Submitting form...' : 'Form ready to submit'}
                </div>
            </form>

            {/* Success Confirmation Modal */}
            {showSuccess && signupData && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true" aria-labelledby="success-title">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative animate-in fade-in-0 zoom-in-95 duration-300">
                        {/* Success Icon */}
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                            <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>

                        {/* Success Content */}
                        <div className="text-center">
                            <h2 id="success-title" className="text-xl font-semibold text-gray-900 mb-2">
                                Signup Successful!
                            </h2>
                            <p className="text-gray-600 mb-6">
                                Your registration has been submitted successfully.
                            </p>

                            {/* Signup Details */}
                            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium text-gray-700">Confirmation #:</span>
                                        <span className="text-sm text-gray-900 font-mono font-bold text-lg">{signupData.signupId}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm font-medium text-gray-700">Total Cost:</span>
                                        <span className="text-sm text-gray-900 font-semibold">${signupData.totalCost.toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={() => {
                                        setShowSuccess(false);
                                        setSignupData(null);
                                    }}
                                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                    Close
                                </button>
                                <button
                                    onClick={() => {
                                        // Copy signup ID to clipboard
                                        navigator.clipboard.writeText(signupData.signupId);
                                        // You could add a toast notification here
                                    }}
                                    className="flex-1 border border-input bg-background hover:bg-accent hover:text-accent-foreground px-4 py-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                >
                                    Copy ID
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
} 